import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { message } from 'antd';
import {
  createAfterSalesType,
  updateAfterSalesType,
  deleteAfterSalesType,
  pageAfterSalesTypes
} from '../../api/afterSalesType';

// 初始状态
const initialState = {
  afterSalesTypes: [],
  total: 0,
  currentPage: 1,
  pageSize: 10,
  loading: false,
  currentAfterSalesType: null
};

// 异步action - 获取售后类型列表
export const fetchAfterSalesTypes = createAsyncThunk(
  'afterSalesType/fetchList',
  async ({ page, pageSize, queryParams }, { rejectWithValue }) => {
    try {
      const response = await pageAfterSalesTypes({
        page,
        pageSize,
        ...queryParams
      });
      return {
        records: response.data.records,
        total: response.data.total
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || '获取售后类型列表失败');
    }
  }
);

// 异步action - 创建售后类型
export const addAfterSalesType = createAsyncThunk(
  'afterSalesType/add',
  async (afterSalesType, { rejectWithValue, dispatch }) => {
    try {
      const response = await createAfterSalesType(afterSalesType);
      message.success('创建售后类型成功');
      // 刷新列表
      dispatch(fetchAfterSalesTypes({
        page: 1,
        pageSize: 10
      }));
      return response.data;
    } catch (error) {
      message.error(error.response?.data?.message || '创建售后类型失败');
      return rejectWithValue(error.response?.data || '创建售后类型失败');
    }
  }
);

// 异步action - 更新售后类型
export const modifyAfterSalesType = createAsyncThunk(
  'afterSalesType/modify',
  async (afterSalesType, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await updateAfterSalesType(afterSalesType);
      message.success('更新售后类型成功');
      // 刷新列表
      const { currentPage, pageSize } = getState().afterSalesType;
      dispatch(fetchAfterSalesTypes({
        page: currentPage,
        pageSize
      }));
      return response.data;
    } catch (error) {
      message.error(error.response?.data?.message || '更新售后类型失败');
      return rejectWithValue(error.response?.data || '更新售后类型失败');
    }
  }
);

// 异步action - 删除售后类型
export const removeAfterSalesType = createAsyncThunk(
  'afterSalesType/remove',
  async (id, { rejectWithValue, dispatch, getState }) => {
    try {
      await deleteAfterSalesType(id);
      message.success('删除售后类型成功');
      // 刷新列表
      const { currentPage, pageSize } = getState().afterSalesType;
      dispatch(fetchAfterSalesTypes({
        page: currentPage,
        pageSize
      }));
      return id;
    } catch (error) {
      message.error(error.response?.data?.message || '删除售后类型失败');
      return rejectWithValue(error.response?.data || '删除售后类型失败');
    }
  }
);

// Slice
const afterSalesTypeSlice = createSlice({
  name: 'afterSalesType',
  initialState,
  reducers: {
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload;
    },
    setCurrentAfterSalesType: (state, action) => {
      state.currentAfterSalesType = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // 获取列表
      .addCase(fetchAfterSalesTypes.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAfterSalesTypes.fulfilled, (state, action) => {
        state.loading = false;
        state.afterSalesTypes = action.payload.records;
        state.total = action.payload.total;
      })
      .addCase(fetchAfterSalesTypes.rejected, (state) => {
        state.loading = false;
      })
      // 创建
      .addCase(addAfterSalesType.pending, (state) => {
        state.loading = true;
      })
      .addCase(addAfterSalesType.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(addAfterSalesType.rejected, (state) => {
        state.loading = false;
      })
      // 更新
      .addCase(modifyAfterSalesType.pending, (state) => {
        state.loading = true;
      })
      .addCase(modifyAfterSalesType.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(modifyAfterSalesType.rejected, (state) => {
        state.loading = false;
      })
      // 删除
      .addCase(removeAfterSalesType.pending, (state) => {
        state.loading = true;
      })
      .addCase(removeAfterSalesType.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(removeAfterSalesType.rejected, (state) => {
        state.loading = false;
      });
  }
});

export const { setCurrentPage, setPageSize, setCurrentAfterSalesType } = afterSalesTypeSlice.actions;

export default afterSalesTypeSlice.reducer;
